/**
 * Stability Detection Utilities
 *
 * Uses requestAnimationFrame to detect when element dimensions are stable
 * across multiple frames. This is more reliable than timeout-based approaches.
 */

import { getElementDimensions, validateDimensions, ElementDimensions } from './dimensionUtils';

export interface StabilityOptions {
  /** Number of consecutive stable frames required (default: 3) */
  requiredFrames?: number;
  /** Maximum change threshold in pixels to consider stable (default: 5) */
  threshold?: number;
  /** Maximum time to wait in milliseconds (default: 2000) */
  maxWaitTime?: number;
  /** Optional predicate to check before considering dimensions stable. If returns false, stability check is reset. */
  shouldProceed?: (dimensions: ElementDimensions) => boolean;
}

/**
 * Detect when element dimensions are stable across multiple animation frames.
 * Returns a cleanup function that cancels the detection.
 *
 * @param element - The element to monitor
 * @param onStable - Callback when dimensions are stable
 * @param options - Configuration options
 * @returns Cleanup function to cancel detection
 */
export function detectStability(
  element: HTMLElement,
  onStable: (dimensions: ElementDimensions) => void,
  options: StabilityOptions = {}
): () => void {
  const {
    requiredFrames = 3,
    threshold = 5,
    maxWaitTime = 2000,
    shouldProceed
  } = options;

  let stableFrames = 0;
  let lastDimensions: ElementDimensions = { width: 0, height: 0 };
  let frameId: number | null = null;
  let startTime = Date.now();
  let cancelled = false;

  const checkDimensions = () => {
    // Check timeout
    if (Date.now() - startTime > maxWaitTime) {
      // Timeout reached - use current dimensions if valid
      const current = getElementDimensions(element);
      if (validateDimensions(current)) {
        // Check shouldProceed predicate if provided
        if (!shouldProceed || shouldProceed(current)) {
          onStable(current);
        }
      }
      return;
    }

    // Check if cancelled
    if (cancelled) {
      return;
    }

    const current = getElementDimensions(element);

    // Check if dimensions are valid
    if (!validateDimensions(current)) {
      // Invalid dimensions - reset stability counter and continue
      stableFrames = 0;
      lastDimensions = current;
      frameId = requestAnimationFrame(checkDimensions);
      return;
    }

    // Check shouldProceed predicate if provided
    // If it returns false, reset stability counter and continue checking
    if (shouldProceed && !shouldProceed(current)) {
      stableFrames = 0;
      lastDimensions = current;
      frameId = requestAnimationFrame(checkDimensions);
      return;
    }

    // Check if dimensions are stable (haven't changed significantly)
    const widthDiff = Math.abs(current.width - lastDimensions.width);
    const heightDiff = Math.abs(current.height - lastDimensions.height);

    if (widthDiff < threshold && heightDiff < threshold) {
      // Dimensions are stable
      stableFrames++;

      if (stableFrames >= requiredFrames) {
        // Stable enough - call callback
        onStable(current);
        return;
      }
    } else {
      // Dimensions changed - reset stability counter
      stableFrames = 0;
      lastDimensions = current;
    }

    // Continue checking
    frameId = requestAnimationFrame(checkDimensions);
  };

  // Start detection
  frameId = requestAnimationFrame(checkDimensions);

  // Return cleanup function
  return () => {
    cancelled = true;
    if (frameId !== null) {
      cancelAnimationFrame(frameId);
      frameId = null;
    }
  };
}

